/* Sticky add to cart — Paolina Roma (Dawn)
   Custom element behind sections/pr-product-sticky-atc.liquid:
   - reveals the bar once the page is scrolled past the trigger
   - hides it again as soon as the footer enters the viewport
     (IntersectionObserver instead of a per-frame getBoundingClientRect)
   - keeps variant id, price, compare-at price and button state in sync
     with the main product form through Dawn's variantChange pub/sub
   Self-initializing so it survives shopify:section:load in the editor.
   Requires constants.js + pubsub.js, already loaded in layout/theme.liquid. */

if (!customElements.get('pr-sticky-atc')) {
  customElements.define(
    'pr-sticky-atc',
    class PrStickyAtc extends HTMLElement {
      connectedCallback() {
        this.trigger = parseInt(this.dataset.trigger, 10) || 400;
        this.label = this.dataset.label || '';
        this.btn = this.querySelector('.pr-sticky-atc__btn');
        this.btnLabel = this.btn ? this.btn.querySelector('span') : null;
        this.variantInput = this.querySelector('.pr-sticky-atc__variant');
        this.priceEl = this.querySelector('.pr-sticky-atc__price');
        this.compareEl = this.querySelector('.pr-sticky-atc__compare');

        this.footerVisible = false;
        this.blocked = false;
        this.shown = null;
        this.ticking = false;

        this.onScroll = this.onScroll.bind(this);

        if (this.dataset.mobile === 'true') {
          document.body.classList.add('pr-has-sticky-atc');
        }

        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll, { passive: true });

        this.watchFooter();
        this.watchOverlays();
        this.watchVariant();
        this.update();
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);

        if (this.observer) {
          this.observer.disconnect();
          this.observer = null;
        }
        if (this.bodyObserver) {
          this.bodyObserver.disconnect();
          this.bodyObserver = null;
        }
        if (typeof this.unsubscribe === 'function') {
          this.unsubscribe();
          this.unsubscribe = null;
        }
        document.body.classList.remove('pr-has-sticky-atc');
      }

      watchFooter() {
        const footer = document.querySelector('.footer') || document.querySelector('footer');
        if (!footer || typeof IntersectionObserver === 'undefined') return;

        this.observer = new IntersectionObserver(
          (entries) => {
            this.footerVisible = entries[0].isIntersecting;
            this.update();
          },
          { threshold: 0 }
        );
        this.observer.observe(footer);
      }

      /* Dawn flags an open drawer/modal with body.overflow-hidden or
         body.overflow-hidden-<breakpoint> (global.js, details-modal.js,
         cart-drawer.js). The CSS drops the bar below the dialog; here it also
         slides out of view and goes inert. The state is folded into update()'s
         single `show` expression on purpose: update() is the only writer of
         [inert], so a second independent writer would be clobbered on the next
         scroll frame. */
      watchOverlays() {
        if (typeof MutationObserver === 'undefined') return;

        this.readOverlayState();
        this.bodyObserver = new MutationObserver(() => {
          this.readOverlayState();
          this.update();
        });
        this.bodyObserver.observe(document.body, {
          attributes: true,
          attributeFilter: ['class'],
        });
      }

      readOverlayState() {
        const body = document.body;
        this.blocked =
          body.classList.contains('overflow-hidden') || /(^|\s)overflow-hidden-/.test(body.className);
      }

      watchVariant() {
        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          this.onVariant((event && event.data) || {});
        });
      }

      onScroll() {
        if (this.ticking) return;
        this.ticking = true;
        window.requestAnimationFrame(() => {
          this.ticking = false;
          this.update();
        });
      }

      update() {
        const show = window.scrollY > this.trigger && !this.footerVisible && !this.blocked;
        if (show === this.shown) return;
        this.shown = show;
        this.setAttribute('aria-hidden', show ? 'false' : 'true');
        this.toggleAttribute('inert', !show);
      }

      onVariant(data) {
        const variant = data.variant;
        const available = Boolean(variant && variant.available);
        const strings = window.variantStrings || {};

        if (this.variantInput) {
          this.variantInput.value = variant ? variant.id : '';
        }
        if (this.btn) {
          this.btn.toggleAttribute('disabled', !available);
        }
        if (this.btnLabel) {
          this.btnLabel.textContent = available
            ? this.label || strings.addToCart || this.btnLabel.textContent
            : strings.soldOut || this.btnLabel.textContent;
        }

        this.updatePrice(data.sectionId);
      }

      /* Reads the price node the main product-info has already refreshed
         (product-info.js updates it before publishing variantChange), so the
         shop's money_format is respected without reformatting anything here.
         Two mutually exclusive markups can live inside #price-<sectionId>:
         Dawn's price snippet, or, when the price block runs with show_bundle
         on and the variant is discounted, Paolina's .pr-bundle markup, which
         has none of Dawn's class names. Each gets its own explicit branch, so
         the sale/regular resolution of the Dawn path stays exactly as it was.
         If the node or its structure is missing, the previous value stays. */
      updatePrice(sectionId) {
        if (!sectionId || !this.priceEl) return;

        const source = document.getElementById(`price-${sectionId}`);
        if (!source) return;

        const bundleNow = source.querySelector('.pr-bundle__now');
        let current;
        let compareNode;

        if (bundleNow) {
          current = bundleNow;
          compareNode = source.querySelector('.pr-bundle__was');
        } else {
          const root = source.querySelector('.price');
          const onSale = Boolean(root && root.classList.contains('price--on-sale'));
          current = source.querySelector(
            onSale ? '.price__sale .price-item--sale' : '.price__regular .price-item--regular'
          );
          compareNode = onSale ? source.querySelector('.price__sale s.price-item--regular') : null;
        }

        if (current) {
          this.priceEl.textContent = current.textContent.trim();
        }

        if (!this.compareEl) return;

        const compare = compareNode ? compareNode.textContent.trim() : '';

        this.compareEl.textContent = compare;
        this.compareEl.toggleAttribute('hidden', !compare);
      }
    }
  );
}
